import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import BusinessPlan from "@/components/BusinessPlan";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function SharedBusinessPlanPage() {
  const { shareToken } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSharedPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  const loadSharedPlan = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/business-plan/shared/${shareToken}`);
      setPlan(response.data);
    } catch (err) {
      console.error("Error loading shared plan:", err);
      if (err.response?.status === 404) {
        setError("This share link is not valid or has been removed.");
      } else if (err.response?.status === 410) {
        setError("This share link has expired.");
      } else {
        setError("Failed to load the business plan. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading business plan...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="text-6xl mb-6">😕</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Oops!</h1>
            <p className="text-gray-600 mb-8">{error}</p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600 transition-colors"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      {/* Simple Header for Shared View */}
      <div className="bg-white shadow-sm border-b border-yellow-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🍋</span>
              <span className="font-bold text-gray-800">Lemonade Stand Business Plan</span>
            </div>
            <Link
              to="/"
              className="text-sm text-yellow-600 hover:text-yellow-700 font-medium"
            >
              Learn More →
            </Link>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Shared Plan Banner */}
        <div className="bg-gradient-to-r from-green-100 to-emerald-100 border border-green-200 rounded-2xl p-6 mb-8 text-center">
          <div className="text-4xl mb-3">👋</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {plan?.student_name}&apos;s Business Plan
          </h1>
          <p className="text-gray-600">
            This business plan was created through the Lemonade Stand Youth Entrepreneurship Program.
          </p>
        </div>

        {/* Business Plan (View Only) */}
        <BusinessPlan 
          plan={plan} 
          studentName={plan?.student_name || "Student"}
          showActions={false}
          canShare={false}
        />

        {/* Footer CTA */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl mx-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-3">
              Want to create your own business plan?
            </h2>
            <p className="text-gray-600 mb-6">
              Join the Lemonade Stand Business Plan program and learn entrepreneurship skills while building your own business!
            </p>
            <Link
              to="/register"
              className="inline-block px-8 py-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-xl font-bold hover:from-yellow-500 hover:to-orange-500 transition-colors shadow-lg"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SharedBusinessPlanPage;
